
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import type { WardrobeItem, AccessorySet } from '../types';
import { CDN_URLS, OCI_FOLDERS } from '../config';

export const defaultWardrobe: WardrobeItem[] = [
  // Garments: Tops
  {
    id: 'top-1',
    nameKey: 'wardrobe_item_white_tshirt',
    url: `${CDN_URLS.UNSPLASH}photo-1521572163474-6864f9cf17ab?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1521572163474-6864f9cf17ab?w=400`,
    type: 'garment',
    category: 'Tops',
    defaultColor: '#FFFFFF',
    colorFamily: ['white', 'neutral'],
    material: '100% Organic Cotton',
    brand: 'Everlane',
  },
  {
    id: 'top-2',
    nameKey: 'wardrobe_item_black_tshirt',
    url: `${CDN_URLS.UNSPLASH}photo-1583743814966-8936f5b7be1a?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1583743814966-8936f5b7be1a?w=400`,
    type: 'garment',
    category: 'Tops',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    material: 'Pima Cotton',
    brand: 'Theory',
  },
  {
    id: 'top-5',
    nameKey: 'wardrobe_item_cream_sweater',
    url: `${CDN_URLS.UNSPLASH}photo-1434389677669-e08b4cac3105?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1434389677669-e08b4cac3105?w=400`,
    type: 'garment',
    category: 'Tops',
    defaultColor: '#F5F5DC',
    colorFamily: ['white', 'beige', 'neutral'],
    material: 'Cashmere Wool Blend',
    brand: 'Vince',
  },
  {
    id: 'top-6',
    nameKey: 'wardrobe_item_silk_blouse',
    url: `${CDN_URLS.UNSPLASH}photo-1485462537746-965f33f7f6a7?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1485462537746-965f33f7f6a7?w=400`,
    type: 'garment',
    category: 'Tops',
    defaultColor: '#FFF5EE',
    colorFamily: ['white', 'beige', 'neutral'],
    requiredMembership: 'premium',
    material: '100% Mulberry Silk',
    brand: 'Zimmermann',
  },
  // Garments: Jackets / Outerwear
  {
    id: 'top-3',
    nameKey: 'wardrobe_item_denim_jacket',
    url: `${CDN_URLS.UNSPLASH}photo-1576995853123-5a10305d93c0?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1576995853123-5a10305d93c0?w=400`,
    type: 'garment',
    category: 'Jackets',
    defaultColor: '#4F86F7',
    colorFamily: ['blue'],
    material: 'Rigid Denim',
    brand: 'Levi\'s Premium',
  },
  {
    id: 'top-7',
    nameKey: 'wardrobe_item_black_blazer',
    url: `${CDN_URLS.UNSPLASH}photo-1591047139829-d91aecb6caea?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1591047139829-d91aecb6caea?w=400`,
    type: 'garment',
    category: 'Jackets',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    requiredMembership: 'premium',
    material: 'Virgin Wool',
    brand: 'Anine Bing',
  },
  {
    id: 'top-8',
    nameKey: 'wardrobe_item_leather_jacket',
    url: `${CDN_URLS.UNSPLASH}photo-1520975954732-35dd22299614?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1520975954732-35dd22299614?w=400`,
    type: 'garment',
    category: 'Jackets',
    defaultColor: '#333333',
    colorFamily: ['black', 'neutral'],
    requiredMembership: 'advanced',
    material: 'Lambskin Leather',
    brand: 'AllSaints',
  },
  {
    id: 'outer-1',
    nameKey: 'wardrobe_item_trench_coat',
    url: `${CDN_URLS.UNSPLASH}photo-1564584217132-2271feaeb3c5?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1564584217132-2271feaeb3c5?w=400`,
    type: 'garment',
    category: 'Jackets',
    defaultColor: '#C4A484',
    colorFamily: ['beige', 'neutral'],
    material: 'Water-resistant Cotton Gabardine',
    brand: 'Burberry',
  },
  {
    id: 'outer-2',
    nameKey: 'wardrobe_item_puffer_jacket',
    url: `${CDN_URLS.UNSPLASH}photo-1551488831-00ddcb6c6bd3?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1551488831-00ddcb6c6bd3?w=400`,
    type: 'garment',
    category: 'Jackets',
    defaultColor: '#000080',
    colorFamily: ['blue'],
    material: 'Nylon / Down Fill',
    brand: 'Moncler',
  },
  {
    id: 'outer-3',
    nameKey: 'wardrobe_item_wool_coat',
    url: `${CDN_URLS.UNSPLASH}photo-1539533018447-63fcce2678e3?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1539533018447-63fcce2678e3?w=400`,
    type: 'garment',
    category: 'Jackets',
    defaultColor: '#C19A6B',
    colorFamily: ['brown', 'beige'],
    material: 'Camel Hair & Wool',
    brand: 'Max Mara',
  },
  // Garments: Dresses
  {
    id: 'dress-1',
    nameKey: 'wardrobe_item_black_dress',
    url: `${CDN_URLS.UNSPLASH}photo-1595777457583-95e059d581b8?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1595777457583-95e059d581b8?w=400`,
    type: 'garment',
    category: 'Dresses',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    requiredMembership: 'advanced',
    material: 'Silk Crepe',
    brand: 'Reformation',
  },
  {
    id: 'dress-2',
    nameKey: 'wardrobe_item_floral_sundress',
    url: `${CDN_URLS.UNSPLASH}photo-1572804013309-59a88b7e92f1?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1572804013309-59a88b7e92f1?w=400`,
    type: 'garment',
    category: 'Dresses',
    colorFamily: ['multi', 'pink', 'green'],
    material: 'Linen Viscose',
    brand: 'Faithfull the Brand',
  },
  // Garments: Bottoms
  {
    id: 'bottom-1',
    nameKey: 'wardrobe_item_blue_jeans',
    url: `${CDN_URLS.UNSPLASH}photo-1542272454315-4c01d7abdf4a?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1542272454315-4c01d7abdf4a?w=400`,
    type: 'garment',
    category: 'Bottoms',
    defaultColor: '#1E3A8A',
    colorFamily: ['blue'],
    material: 'Organic Denim',
    brand: 'Agolde',
  },
  {
    id: 'bottom-2',
    nameKey: 'wardrobe_item_black_trousers',
    url: `${CDN_URLS.UNSPLASH}photo-1594633312681-425c7b97ccd1?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1594633312681-425c7b97ccd1?w=400`,
    type: 'garment',
    category: 'Bottoms',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    material: 'Italian Wool',
    brand: 'Theory',
  },
  {
    id: 'bottom-3',
    nameKey: 'wardrobe_item_black_skirt',
    url: `${CDN_URLS.UNSPLASH}photo-1525507119028-ed4c629a60a3?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1525507119028-ed4c629a60a3?w=400`,
    type: 'garment',
    category: 'Bottoms',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    material: 'Silk Satin',
    brand: 'Realisation Par',
  },
  {
    id: 'bottom-4',
    nameKey: 'wardrobe_item_beige_shorts',
    url: `${CDN_URLS.UNSPLASH}photo-1591195853828-11db59a44f6b?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1591195853828-11db59a44f6b?w=400`,
    type: 'garment',
    category: 'Bottoms',
    defaultColor: '#F5F5DC',
    colorFamily: ['beige', 'neutral'],
    material: 'Linen Blend',
    brand: 'Club Monaco',
  },
  // Shoes (Type: Accessory, but displayed under Garments tab)
  {
    id: 'shoe-1',
    nameKey: 'wardrobe_item_black_heels',
    url: `${CDN_URLS.UNSPLASH}photo-1543163521-1bf539c55dd2?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1543163521-1bf539c55dd2?w=400`,
    type: 'accessory',
    category: 'Shoes',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    material: 'Patent Leather',
    brand: 'Christian Louboutin',
  },
  {
    id: 'shoe-2',
    nameKey: 'wardrobe_item_white_sneakers',
    url: `${CDN_URLS.UNSPLASH}photo-1549298916-b41d501d3772?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1549298916-b41d501d3772?w=400`,
    type: 'accessory',
    category: 'Shoes',
    defaultColor: '#FFFFFF',
    colorFamily: ['white', 'neutral'],
    material: 'Leather & Rubber',
    brand: 'Common Projects',
  },
  {
    id: 'shoe-3',
    nameKey: 'wardrobe_item_brown_boots',
    url: `${CDN_URLS.UNSPLASH}photo-1608256246200-53e635b5b65f?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1608256246200-53e635b5b65f?w=400`,
    type: 'accessory',
    category: 'Shoes',
    defaultColor: '#8B4513',
    colorFamily: ['brown', 'neutral'],
    material: 'Full Grain Leather',
    brand: 'Frye',
  },
  // Accessories: Bags
  {
    id: 'acc-1',
    nameKey: 'wardrobe_item_brown_tote',
    url: `${CDN_URLS.UNSPLASH}photo-1591561954557-26941169b49e?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1591561954557-26941169b49e?w=400`,
    type: 'accessory',
    category: 'Bags',
    defaultColor: '#8B4513',
    colorFamily: ['brown', 'neutral'],
    material: 'Saffiano Leather',
    brand: 'Prada',
  },
  {
    id: 'acc-5',
    nameKey: 'wardrobe_item_black_handbag',
    url: `${CDN_URLS.UNSPLASH}photo-1584917865442-de89df76afd3?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1584917865442-de89df76afd3?w=400`,
    type: 'accessory',
    category: 'Bags',
    defaultColor: '#000000',
    colorFamily: ['black', 'neutral'],
    material: 'Quilted Leather',
    brand: 'YSL',
  },
  // Accessories: Glasses
  {
    id: 'acc-2',
    nameKey: 'wardrobe_item_brown_sunglasses',
    url: `${CDN_URLS.UNSPLASH}photo-1572635196237-14b3f281503f?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1572635196237-14b3f281503f?w=400`,
    type: 'accessory',
    category: 'Eyewear',
    defaultColor: '#5C4033',
    colorFamily: ['brown', 'neutral'],
    material: 'Acetate',
    brand: 'Celine',
  },
  // Accessories: Jewelry
  {
    id: 'acc-3',
    nameKey: 'wardrobe_item_pearl_necklace',
    url: `${CDN_URLS.UNSPLASH}photo-1515562141207-7a88fb7ce338?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1515562141207-7a88fb7ce338?w=400`,
    type: 'accessory',
    category: 'Jewelry',
    defaultColor: '#F0EAD6',
    colorFamily: ['white', 'neutral'],
    material: 'Freshwater Pearl',
    brand: 'Mejuri',
  },
  {
    id: 'acc-4',
    nameKey: 'wardrobe_item_silver_watch',
    url: `${CDN_URLS.UNSPLASH}photo-1524592094714-0f0654e20314?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1524592094714-0f0654e20314?w=400`,
    type: 'accessory',
    category: 'Jewelry',
    defaultColor: '#C0C0C0',
    colorFamily: ['grey', 'neutral'],
    material: 'Stainless Steel',
    brand: 'Omega',
  },
  {
    id: 'acc-9',
    nameKey: 'wardrobe_item_pearl_earrings',
    url: `${CDN_URLS.UNSPLASH}photo-1535632066927-ab7c9ab60908?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1535632066927-ab7c9ab60908?w=400`,
    type: 'accessory',
    category: 'Jewelry',
    defaultColor: '#F0EAD6',
    colorFamily: ['white', 'neutral'],
    requiredMembership: 'premium',
    material: 'Gold Vermeil & Pearl',
    brand: 'Missoma',
  },
  // Accessories: Other
  {
    id: 'acc-6',
    nameKey: 'wardrobe_item_red_scarf',
    url: `${CDN_URLS.UNSPLASH}photo-1543076447-215ad9ba6923?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1543076447-215ad9ba6923?w=400`,
    type: 'accessory',
    category: 'Other',
    defaultColor: '#FF0000',
    colorFamily: ['red'],
    material: 'Cashmere',
    brand: 'Acne Studios',
  },
  {
    id: 'acc-7',
    nameKey: 'wardrobe_item_fedora_hat',
    url: `${CDN_URLS.UNSPLASH}photo-1533827432537-70133748f5c8?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1533827432537-70133748f5c8?w=400`,
    type: 'accessory',
    category: 'Other',
    defaultColor: '#5C4033',
    colorFamily: ['brown', 'neutral'],
    material: 'Wool Felt',
    brand: 'Lack of Color',
  },
  {
    id: 'acc-8',
    nameKey: 'wardrobe_item_brown_belt',
    url: `${CDN_URLS.UNSPLASH}photo-1553062407-98eeb64c6a62?w=400`,
    iconUrl: `${CDN_URLS.UNSPLASH}photo-1553062407-98eeb64c6a62?w=400`,
    type: 'accessory',
    category: 'Other',
    defaultColor: '#8B4513',
    colorFamily: ['brown', 'neutral'],
    material: 'Calfskin Leather',
    brand: 'Gucci',
  },
  // Elegant Collection (Premium)
  {
    id: 'elegant-1',
    nameKey: 'wardrobe_item_elegant_blue',
    url: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_blau.png`,
    iconUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_blau.png`,
    type: 'garment',
    category: 'Dresses',
    requiredMembership: 'premium',
    brand: 'Constantina Elegant',
  },
  {
    id: 'elegant-2',
    nameKey: 'wardrobe_item_elegant_black_stairs',
    url: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_schwarz_stairs.png`,
    iconUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_schwarz_stairs.png`,
    type: 'garment',
    category: 'Dresses',
    requiredMembership: 'premium',
    brand: 'Constantina Elegant',
  },
  {
    id: 'elegant-3',
    nameKey: 'wardrobe_item_elegant_wool_coat',
    url: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_mantel2.png`,
    iconUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_mantel2.png`,
    type: 'garment',
    category: 'Jackets',
    requiredMembership: 'premium',
    brand: 'Constantina Elegant',
  },
  {
    id: 'elegant-4',
    nameKey: 'wardrobe_item_elegant_gray_studio',
    url: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_gray_art_studio.png`,
    iconUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_gray_art_studio.png`,
    type: 'garment',
    category: 'Dresses',
    requiredMembership: 'premium',
    brand: 'Constantina Elegant',
  },
  {
    id: 'elegant-5',
    nameKey: 'wardrobe_item_elegant_autumn',
    url: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/city_herbst_blau_elegance.png`,
    iconUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/city_herbst_blau_elegance.png`,
    type: 'garment',
    category: 'Tops',
    requiredMembership: 'premium',
    brand: 'Constantina Elegant',
  },
];

export const predefinedAccessorySets: AccessorySet[] = [
  {
    id: 'set-1',
    nameKey: 'accessory_set_everyday_essentials',
    accessoryIds: ['acc-1', 'acc-2', 'acc-4'],
    previewImageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Casual/city_style.png`,
  },
  {
    id: 'set-2',
    nameKey: 'accessory_set_city_sleek',
    accessoryIds: ['acc-5', 'acc-2', 'acc-3'],
    previewImageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}city/schwarz_el_bern.png`,
  },
  {
    id: 'set-elegant-1',
    nameKey: 'accessory_set_elegant_collection',
    accessoryIds: [],
    garmentIds: ['elegant-1', 'elegant-2', 'elegant-3', 'elegant-4', 'elegant-5'],
    previewImageUrl: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_schwarz_stairs.png`,
  },
];
